<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="760px">
    <v-card v-if="Student" class="profile-dialog-card entity-form-dialog">
      <EntityDialogHeader eyebrow="Student enrollment" :title="Student.fullName" subtitle="Manage classrooms" icon="mdi-google-classroom" close-label="Close classrooms" @close="close" />
      <v-divider />
      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-google-classroom</v-icon></span><div><h3>Current classrooms ({{ assignments.length }})</h3><p>Remove a classroom assignment when it ends.</p></div></div>
          <div v-if="loading" class="text-center py-4"><v-progress-circular indeterminate color="primary" /></div>
          <div v-else-if="assignments.length">
            <div v-for="item in assignments" :key="item.id" class="student-management-row">
              <div><strong>{{ item.ClassRoom ? item.ClassRoom.name : "Classroom" }}</strong><small>{{ item.ClassRoom && item.ClassRoom.Class ? item.ClassRoom.Class.name : "Department unavailable" }}</small></div>
              <v-btn icon small color="error" aria-label="Remove classroom" @click="remove(item)"><v-icon size="18">mdi-delete-outline</v-icon></v-btn>
            </div>
          </div>
          <p v-else class="mb-0">No classrooms assigned.</p>
        </section>
        <section v-if="!Student.leftDate" class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-plus</v-icon></span><div><h3>Add a classroom</h3><p>Choose from classrooms in the student's departments.</p></div></div>
          <v-autocomplete v-model="selectedClassRoom" :search-input.sync="classroomSearch" :items="availableClassrooms" :filter="filterClassroom" item-text="name" return-object outlined dense clearable hide-details label="Classroom or teacher" :loading="loading" no-data-text="No matching classrooms in the student's departments">
            <template v-slot:selection="{ item }"><span class="classroom-selection">{{ item.name }} · {{ departmentName(item) }} · {{ teacherNames(item) || "No teacher assigned" }}</span></template>
            <template v-slot:item="{ item }"><div class="classroom-option"><strong><SearchHighlight :text="item.name" :query="classroomSearch" /></strong><small><SearchHighlight :text="departmentName(item)" :query="classroomSearch" /></small><small>Teachers: <SearchHighlight :text="teacherNames(item) || 'Not assigned'" :query="classroomSearch" /></small></div></template>
          </v-autocomplete>
          <div class="d-flex justify-end mt-3"><v-btn color="primary" depressed :loading="saving" :disabled="!selectedClassRoom" @click="submit">Add classroom</v-btn></div>
        </section>
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-timeline-clock-outline</v-icon></span><div><h3>Classroom history ({{ history.length }})</h3><p>Current and former classroom assignments.</p></div></div>
          <div v-if="history.length">
            <div v-for="item in history" :key="item.id" class="student-management-row">
              <div><strong>{{ item.ClassRoom ? item.ClassRoom.name : "Classroom unavailable" }}</strong><small>Assigned {{ formatDate(item.createdAt) }}<template v-if="item.deletedAt"> · Removed {{ formatDate(item.deletedAt) }}</template></small></div>
              <v-chip x-small :color="item.deletedAt ? 'grey' : 'primary'" outlined>{{ item.deletedAt ? "Former" : "Current" }}</v-chip>
            </div>
          </div>
          <p v-else class="mb-0">No classroom history recorded yet.</p>
        </section>
      </v-card-text>
      <v-divider />
      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions"><v-btn text @click="close">Close</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import classRoomService from "@/modules/ClassRoom/service.js";
import studentService from "@/modules/Student/service.js";
import SearchHighlight from "@/modules/ClassRoom/components/SearchHighlight.vue";

export default {
  name: "ManageStudentClassrooms",
  components: { SearchHighlight },
  props: { editDialog: { default: false }, Student: { type: Object, default: null } },
  data() { return { dialog: false, classrooms: [], departments: [], assignments: [], history: [], selectedClassRoom: null, classroomSearch: "", loading: false, saving: false }; },
  computed: {
    availableClassrooms() {
      const departmentIds = new Set(this.departments.map((row) => Number(row.ClassId)));
      return this.classrooms.filter((room) => departmentIds.has(Number(room.ClassId)) &&
        !this.assignments.some((row) => Number(row.ClassRoomId) === Number(room.id)));
    },
  },
  watch: { editDialog(open) { this.dialog = open; if (open && this.Student) this.load(); else if (!open) this.selectedClassRoom = null; } },
  methods: {
    departmentName(room) { return room.Class ? room.Class.name : "Department unavailable"; },
    teacherNames(room) {
      return (room.ClassRoomTeachers || []).filter((row) => row && row.Teacher)
        .map((row) => row.Teacher.name).join(", ");
    },
    filterClassroom(room, search) {
      const query = String(search || "").trim().toLowerCase();
      return [room.name, this.departmentName(room), this.teacherNames(room)]
        .some((value) => String(value || "").toLowerCase().includes(query));
    },
    async load() {
      this.loading = true;
      this.classrooms = [];
      this.departments = [];
      this.assignments = [];
      this.history = [];
      this.selectedClassRoom = null;
      this.classroomSearch = "";
      try {
        const [classrooms, departments, assignments, history] = await Promise.all([
          classRoomService.getAll(), studentService.getAllClassStudent({ StudentId: this.Student.id }),
          studentService.getStudentByClassRoomId({ StudentId: this.Student.id }),
          studentService.getClassroomHistory(this.Student.id),
        ]);
        this.classrooms = classrooms.data || [];
        this.departments = departments.data || [];
        this.assignments = assignments.data || [];
        this.history = history.data || [];
      } catch (error) { this.showError(error, "Could not load classroom assignments."); }
      finally { this.loading = false; }
    },
    async refreshAfterChange() { await this.load(); this.$emit("submit"); },
    async remove(item) {
      const result = await this.$swal({ title: "Remove classroom?", text: `Remove ${this.Student.fullName} from ${item.ClassRoom ? item.ClassRoom.name : "this classroom"}?`, icon: "question", showCancelButton: true, confirmButtonColor: "#B71C1C", confirmButtonText: "Remove" });
      if (!result.value) return;
      try { await studentService.removeStudentFromClassRoom(item.id); await this.refreshAfterChange(); }
      catch (error) { this.showError(error, "Could not remove classroom."); }
    },
    async submit() {
      if (!this.selectedClassRoom) return;
      this.saving = true;
      try { await classRoomService.assignStudent({ ClassRoomId: this.selectedClassRoom.id, StudentId: this.Student.id }); this.selectedClassRoom = null; await this.refreshAfterChange(); }
      catch (error) { this.showError(error, "Could not add classroom."); }
      finally { this.saving = false; }
    },
    formatDate(value) { return value ? this.$moment(value).format("MMM D, YYYY") : "Date unavailable"; },
    showError(error, fallback) { this.$swal("Classroom management", error && error.data && error.data.message ? error.data.message : fallback, "error"); },
    close() { this.dialog = false; this.selectedClassRoom = null; this.$emit("close"); },
  },
};
</script>

<style scoped>
.classroom-option { display: flex; min-width: 0; flex-direction: column; padding: 6px 0; }
.classroom-option strong { color: #28362c; }
.classroom-option small { color: #68766c; }
.classroom-selection { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.student-management-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid #e6ece7; }
.student-management-row:last-child { border-bottom: 0; }
.student-management-row > div { min-width: 0; flex: 1; }
.student-management-row strong, .student-management-row small { display: block; }
.student-management-row small { color: #78857b; }
</style>
